// UserProfileSettingsPage.tsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  IconButton,
  Chip,
  Paper,
  TextField,
  FormControl,
  FormHelperText,
  Grid,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import {
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import userInfoService from "@/services/userService";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuthStore } from "@/store/useAuthStore";

// Dynamic schema generator for i18n dynamic validation messages
const createProfileSchema = (t: (key: string) => string) =>
  z.object({
    firstName: z.string().min(1, t("pages.userProfileSettings.validation.firstNameRequired")),
    lastName: z.string().min(1, t("pages.userProfileSettings.validation.lastNameRequired")),
    phoneNumber: z
      .string()
      .regex(/^\+?[0-9\s\-()]{7,20}$/, t("pages.userProfileSettings.validation.invalidPhone"))
      .optional()
      .or(z.literal("")),
    location: z.string().optional(),
    bio: z.string().max(300, t("pages.userProfileSettings.validation.bioMaxLength")).optional(),
    company: z.string().optional(),
    position: z.string().optional(),
    department: z.string().optional(),
    linkedIn: z.string().optional().or(z.literal("")),
    avatarUrl: z.string().optional(),
  });

export type ProfileFormValues = z.infer<ReturnType<typeof createProfileSchema>>;

export const UserProfileSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState<boolean>(true);
  // const [avatarUrl, setAvatarUrl] = useState<string>("");
  const { avatarUrl, setAvatarUrl, fetchAvatar } = useAuthStore();
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(createProfileSchema(t)),
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      location: "",
      bio: "",
      company: "",
      position: "",
      department: "",
      linkedIn: "",
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");
  const watchedPosition = watch("position");
  const watchedCompany = watch("company");
  const watchedDepartment = watch("department");

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const profile = await userInfoService.getProfile();
      reset(profile);
    } catch (error) {
      console.error("Failed to fetch user info profile:", error);
      showToast(t("pages.userProfileSettings.toast.fetchFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await userInfoService.uploadProfilePic(file);
      const newUrl = await userInfoService.getProfilePicUrl();
      setAvatarUrl(newUrl);
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  useEffect(() => {
    fetchProfile();
    if (!avatarUrl) {
      fetchAvatar();
    }
  }, []);

  // useEffect(() => {
  //   let isMounted = true;
  //   let objectUrl: string | null = null;
  //   const loadAvatar = async () => {
  //     try {
  //       const url = await userInfoService.getProfilePicUrl();
  //       if (isMounted) {
  //         objectUrl = url;
  //         setAvatarUrl(url);
  //       } else {
  //         // If component unmounted while fetching, clean up immediately
  //         URL.revokeObjectURL(url);
  //       }
  //     } catch (err) {
  //       // Profile picture might not exist yet for this user; fail silently or set fallback
  //       console.log("No profile picture found or failed to load.");
  //       console.log(err);
  //     }
  //   };
  //   loadAvatar();
  //   return () => {
  //     isMounted = false;
  //     if (objectUrl) {
  //       URL.revokeObjectURL(objectUrl);
  //     }
  //   };
  // }, []);

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      const profile = await userInfoService.updateProfile(data);
      reset(profile);
      showToast(t("pages.userProfileSettings.toast.updateSuccess"), "success");
    } catch (error) {
      console.error("Failed to update user info profile:", error);
      showToast(t("pages.userProfileSettings.toast.updateFailed"), "error");
    }
  };

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 6, color: "text.primary" }}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Header Hero Banner Card */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 1,
            bgcolor: "background.paper",
            borderColor: "divider",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ alignItems: "center" }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={avatarUrl || "/logo.png"}
                alt={`${watchedFirstName} ${watchedLastName}`}
                sx={{ width: 100, height: 100, fontSize: "2rem", bgcolor: "primary.main" }}
              >
                {watchedFirstName ? watchedFirstName[0]?.toUpperCase() : <PersonIcon sx={{ fontSize: "2.5rem" }} />}
              </Avatar>
              <IconButton
                color="primary"
                aria-label={t("pages.userProfileSettings.header.uploadPicture")}
                component="label"
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                  "&:hover": { bgcolor: isDark ? alpha(theme.palette.primary.main, 0.2) : "grey.100" },
                }}
              >
                <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                <PhotoCameraIcon sx={{ fontSize: "1.25rem" }} />
              </IconButton>
            </Box>

            <Box sx={{ textAlign: { xs: "center", sm: "left" }, flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                {watchedFirstName || watchedLastName
                  ? `${watchedFirstName || ""} ${watchedLastName || ""}`.trim()
                  : t("pages.userProfileSettings.header.defaultTitle")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                {watchedPosition
                  ? watchedCompany
                    ? t("pages.userProfileSettings.header.positionWithCompany", {
                        position: watchedPosition,
                        company: watchedCompany,
                      })
                    : watchedPosition
                  : t("pages.userProfileSettings.header.noPosition")}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}>
                <Chip
                  label={t("pages.userProfileSettings.header.statusActive")}
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                />
                {watchedDepartment && (
                  <Chip
                    label={watchedDepartment}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1, color: "text.primary", borderColor: "divider" }}
                  />
                )}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={fetchProfile}
                disabled={isSubmitting}
                sx={{ borderRadius: 1, borderColor: "divider" }}
              >
                {t("pages.userProfileSettings.actions.reset")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={isSubmitting || !isDirty}
                sx={{ borderRadius: 1 }}
              >
                {isSubmitting ? t("pages.userProfileSettings.actions.saving") : t("pages.userProfileSettings.actions.save")}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* MUI v6 Grid2 Form Structure */}
        <Grid container spacing={3}>
          {/* Personal Information Section */}
          <Grid size={12}>
            <Card variant="outlined" sx={{ borderRadius: 1, bgcolor: "background.paper", borderColor: "divider" }}>
              <CardHeader
                avatar={<PersonIcon color="primary" />}
                title={t("pages.userProfileSettings.sections.personalInfo.title")}
                subheader={t("pages.userProfileSettings.sections.personalInfo.subtitle")}
                slotProps={{
                  title: { sx: { color: "text.primary", fontWeight: 700 } },
                  subheader: { sx: { color: "text.secondary" } },
                }}
              />
              <Divider sx={{ borderColor: "divider" }} />
              <CardContent>
                <Grid container spacing={2}>
                  {/* First Name */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.firstName.label")}
                          placeholder={t("pages.userProfileSettings.fields.firstName.placeholder")}
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.lastName.label")}
                          placeholder={t("pages.userProfileSettings.fields.lastName.placeholder")}
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  {/* Phone Number */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.phoneNumber.label")}
                          placeholder={t("pages.userProfileSettings.fields.phoneNumber.placeholder")}
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Location */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.location.label")}
                          placeholder={t("pages.userProfileSettings.fields.location.placeholder")}
                          error={!!errors.location}
                          helperText={errors.location?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Bio */}
                  <Grid size={12}>
                    <Controller
                      name="bio"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.bio}>
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={3}
                            label={t("pages.userProfileSettings.fields.bio.label")}
                            placeholder={t("pages.userProfileSettings.fields.bio.placeholder")}
                            error={!!errors.bio}
                          />
                          <FormHelperText>
                            {errors.bio?.message ||
                              t("pages.userProfileSettings.fields.bio.charCount", {
                                count: field.value?.length || 0,
                                max: 300,
                              })}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Professional Information Section */}
          <Grid size={12}>
            <Card variant="outlined" sx={{ borderRadius: 1, bgcolor: "background.paper", borderColor: "divider" }}>
              <CardHeader
                avatar={<WorkIcon color="primary" />}
                title={t("pages.userProfileSettings.sections.professionalInfo.title")}
                subheader={t("pages.userProfileSettings.sections.professionalInfo.subtitle")}
                slotProps={{
                  title: { sx: { color: "text.primary", fontWeight: 700 } },
                  subheader: { sx: { color: "text.secondary" } },
                }}
              />
              <Divider sx={{ borderColor: "divider" }} />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Company */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="company"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.company.label")}
                          placeholder={t("pages.userProfileSettings.fields.company.placeholder")}
                          error={!!errors.company}
                          helperText={errors.company?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Position */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="position"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.position.label")}
                          placeholder={t("pages.userProfileSettings.fields.position.placeholder")}
                          error={!!errors.position}
                          helperText={errors.position?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Department */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.department.label")}
                          placeholder={t("pages.userProfileSettings.fields.department.placeholder")}
                          error={!!errors.department}
                          helperText={errors.department?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Website / LinkedIn */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="linkedIn"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t("pages.userProfileSettings.fields.linkedIn.label")}
                          placeholder={t("pages.userProfileSettings.fields.linkedIn.placeholder")}
                          error={!!errors.linkedIn}
                          helperText={errors.linkedIn?.message}
                          slotProps={{
                            input: {
                              startAdornment: <PublicIcon color="action" sx={{ mr: 1 }} />,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      <Box sx={{ mt: 4 }}>
        <LanguageSwitcher />
      </Box>

      {/* Snackbar Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
